import React from 'react';
import { Skeleton } from 'antd';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { visuallyHidden } from '../visuallyHidden';

export type CardSkeletonShape = 'kpis' | 'chart' | 'list' | 'funnel';

export interface ICardSkeletonProps {
  /** Follows the body the card will show once it resolves, so the layout does not jump. */
  shape: CardSkeletonShape;
  /** Placeholder count for the repeating shapes; ignored by `chart`. */
  rows?: number;
  className?: string;
}

const DEFAULT_ROWS: Record<CardSkeletonShape, number> = {
  kpis: 3,
  chart: 1,
  list: 4,
  funnel: 4
};

const Frame = styled.div`
  .ant-skeleton-title,
  .ant-skeleton-paragraph > li,
  .ant-skeleton-element .ant-skeleton-button {
    background: ${tokens.color['track-bg']};
  }

  /* antd 4.24 blanks an active placeholder to background: transparent and paints the shimmer on a
     ::after layer above it; these selectors carry one extra class of specificity so the dark track
     wins back the element underneath. */
  .ant-skeleton.ant-skeleton-active .ant-skeleton-content .ant-skeleton-title,
  .ant-skeleton.ant-skeleton-active .ant-skeleton-content .ant-skeleton-paragraph > li,
  .ant-skeleton.ant-skeleton-active.ant-skeleton-element .ant-skeleton-button {
    background: ${tokens.color['track-bg']};
  }

  /* The shimmer is a translateX keyframe on that ::after layer, and animation is not inherited, so
     stopping it on the element alone leaves a reduce-motion user with a permanent loop. */
  @media (prefers-reduced-motion: reduce) {
    .ant-skeleton.ant-skeleton-active .ant-skeleton-content .ant-skeleton-title::after,
    .ant-skeleton.ant-skeleton-active .ant-skeleton-content .ant-skeleton-paragraph > li::after,
    .ant-skeleton.ant-skeleton-active.ant-skeleton-element .ant-skeleton-button::after {
      animation: none;
    }
  }
`;

const HiddenStatus = styled.span`
  ${visuallyHidden}
`;

const TileRow = styled.div`
  display: flex;
  align-items: stretch;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

const Tile = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-card']};
`;

const Stack = styled.div`
  > * + * {
    margin-top: ${tokens.space['12']};
  }
`;

const Plot = styled.div`
  .ant-skeleton-element,
  .ant-skeleton-button {
    width: 100%;
    height: 180px;
    border-radius: ${tokens.radius.md};
  }
`;

const StepRow = styled.div`
  display: flex;
  align-items: stretch;

  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

const Step = styled.div`
  flex: 1 1 0;
  min-width: 0;

  .ant-skeleton-element,
  .ant-skeleton-button {
    width: 100%;
    height: 92px;
    border-radius: ${tokens.radius.sm};
  }
`;

const placeholderKeys = (count: number): string[] =>
  Array.from({ length: Math.max(1, count) }, (unused, index) => `placeholder-${index}`);

const CardSkeleton: React.FC<ICardSkeletonProps> = ({ shape, rows, className }) => {
  const { t } = useMultiLanguageTranslation();

  const count = Number.isFinite(rows) ? Math.max(1, Math.floor(rows)) : DEFAULT_ROWS[shape];
  const keys = placeholderKeys(count);

  const renderBody = (): React.ReactNode => {
    if (shape === 'kpis') {
      return (
        <TileRow>
          {keys.map(key => (
            <Tile key={key} data-testid="card-skeleton-tile">
              <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: '80%' }} />
            </Tile>
          ))}
        </TileRow>
      );
    }

    if (shape === 'funnel') {
      return (
        <StepRow>
          {keys.map(key => (
            <Step key={key} data-testid="card-skeleton-step">
              <Skeleton.Button active block />
            </Step>
          ))}
        </StepRow>
      );
    }

    if (shape === 'chart') {
      return (
        <Plot data-testid="card-skeleton-plot">
          <Skeleton.Button active block />
        </Plot>
      );
    }

    return (
      <Stack>
        {keys.map(key => (
          <div key={key} data-testid="card-skeleton-row">
            <Skeleton active title={false} paragraph={{ rows: 1, width: '90%' }} />
          </div>
        ))}
      </Stack>
    );
  };

  return (
    <Frame className={className} role="status" data-testid="card-skeleton" data-shape={shape}>
      <HiddenStatus>{t(`${TCustomerInsights}.DataStates.Loading`)}</HiddenStatus>
      {renderBody()}
    </Frame>
  );
};

export default CardSkeleton;
